import { useState } from "react";
import { useStoreQuery, queries, type VerseTheme } from "@/lib/store";
import { Btn, PillTag } from "./UI";
import VerseCheckInSheet from "./VerseCheckInSheet";

/**
 * VerseOfWeekCard \u2014 weekly scripture surface used on Today and Sisters.
 * Two visual variants:
 *   - "today"     full hero card on the Today screen, gold accent, primary CTA
 *   - "compact"   slim card for the Sisters/community feed, neutral surface
 *
 * Both pull from the same currentVerseOfWeek query so sisters see the same
 * verse, the same week. Check-in opens a sheet for an optional reflection.
 */
interface Props {
  userId: string;
  variant?: "today" | "compact";
}

const THEME_LABEL: Record<VerseTheme, string> = {
  strength: "Strength",
  rest: "Rest",
  identity: "Identity",
  hope: "Hope",
  surrender: "Surrender",
  presence: "Presence",
};

export default function VerseOfWeekCard({ userId, variant = "today" }: Props) {
  const verse = useStoreQuery(() => queries.currentVerseOfWeek());
  const myCheckIn = useStoreQuery(() => queries.verseCheckInForUser(userId));
  const checkInCount = useStoreQuery(() => queries.verseCheckInCountThisWeek());
  const [open, setOpen] = useState(false);

  if (!verse) return null;

  const checkedIn = !!myCheckIn;
  const sistersCopy =
    checkInCount === 0
      ? "Be the first to sit with this verse"
      : `${checkInCount} sister${checkInCount === 1 ? "" : "s"} sitting with this verse`;

  if (variant === "compact") {
    return (
      <>
        <div
          data-testid="card-verse-of-week-compact"
          style={{
            position: "relative",
            borderRadius: 18,
            padding: "16px 16px 14px",
            background: "rgba(255,255,255,0.7)",
            border: "1px solid rgba(160,120,32,0.22)",
            overflow: "hidden",
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <PillTag tone="gold">Verse of the week</PillTag>
            <span
              className="mnf-eyebrow"
              style={{ color: "var(--mnf-tg)", fontSize: 9.5 }}
            >
              {THEME_LABEL[verse.theme]}
            </span>
          </div>
          <p
            className="mnf-serif italic"
            style={{
              fontSize: 15.5,
              lineHeight: 1.45,
              color: "var(--mnf-fd)",
              marginBottom: 6,
            }}
          >
            "{verse.text}"
          </p>
          <p
            className="mnf-eyebrow"
            style={{ color: "var(--mnf-t)", fontSize: 10, marginBottom: 12 }}
          >
            {verse.reference}
          </p>
          <div className="flex items-center justify-between gap-3">
            <span
              className="mnf-body"
              style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}
            >
              {sistersCopy}
            </span>
            <button
              onClick={() => setOpen(true)}
              data-testid="button-verse-checkin-compact"
              className="mnf-body hover-elevate"
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                background: checkedIn ? "var(--mnf-gold2)" : "var(--mnf-f)",
                color: checkedIn ? "var(--mnf-gold)" : "#F5F0E8",
                border: checkedIn
                  ? "1px solid rgba(160,120,32,0.28)"
                  : "1px solid var(--mnf-f)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              {checkedIn ? "Reflected" : "Sit with it"}
            </button>
          </div>
        </div>
        {open && <VerseCheckInSheet userId={userId} onClose={() => setOpen(false)} />}
      </>
    );
  }

  // Today / hero variant
  return (
    <>
      <div
        data-testid="card-verse-of-week"
        style={{
          position: "relative",
          borderRadius: 22,
          padding: "20px 20px 22px",
          background:
            "linear-gradient(155deg, rgba(245,240,232,0.92) 0%, rgba(245,240,232,0.7) 60%, rgba(160,120,32,0.10) 100%)",
          border: "1px solid rgba(160,120,32,0.22)",
          overflow: "hidden",
        }}
      >
        {/* gold halo */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: -50,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(160,120,32,0.22) 0%, rgba(160,120,32,0) 65%)",
            pointerEvents: "none",
          }}
        />

        <div className="flex items-center justify-between mb-3 relative">
          <PillTag tone="gold">Verse of the week</PillTag>
          <span
            className="mnf-eyebrow"
            style={{ color: "var(--mnf-t)", fontSize: 10 }}
          >
            {THEME_LABEL[verse.theme]}
          </span>
        </div>

        <blockquote
          className="mnf-serif italic"
          style={{
            fontSize: 19,
            lineHeight: 1.4,
            color: "var(--mnf-fd)",
            margin: 0,
            marginBottom: 10,
            position: "relative",
          }}
        >
          "{verse.text}"
        </blockquote>

        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-t)", fontSize: 10.5, marginBottom: 14 }}
        >
          {verse.reference}
        </p>

        <p
          className="mnf-body"
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--mnf-tx)",
            marginBottom: 14,
            position: "relative",
          }}
        >
          {verse.reflection}
        </p>

        <div
          className="flex items-center justify-between gap-3 pt-3"
          style={{ borderTop: "1px solid rgba(30,74,53,0.10)", position: "relative" }}
        >
          <span
            className="mnf-body"
            style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}
          >
            {sistersCopy}
          </span>
          <Btn
            variant={checkedIn ? "ghost" : "primary"}
            size="sm"
            onClick={() => setOpen(true)}
            data-testid="button-verse-checkin-today"
          >
            {checkedIn ? "Reflected" : "Sit with it"}
          </Btn>
        </div>
      </div>
      {open && <VerseCheckInSheet userId={userId} onClose={() => setOpen(false)} />}
    </>
  );
}
